// Global Instructions Rule Applied!

import {
  validateFilterOptions,
  type TableFilterOptionsResponse
} from '../../../state_manager/rpc_models/get_table_filter_options_response.ts'
import {
  validateAccountRecord,
  type AccountRecord,
  AccountRecordFields
} from '../../../state_manager/data_models/account_record.ts'
import {
  validateAccountMobileListRecord,
  type AccountMobileListRecord
} from '../../../state_manager/data_models/account_mobile_list_record.ts'
import { FilterValue, SortConfig } from '../../../state_manager/store_models/companies.ts'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { supabase } from '../../../core/lib/supabase'
import {
  validateInvestmentMeasureRecord,
  type InvestmentMeasureRecord
} from '../../../state_manager/data_models/investment_measure_record.ts'
import { formatKeyToLabel, readBackendErrorMessage } from '../../../core/utils/utilities.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { TOAST_CONFIG } from '../../../core/components/FormTable/constants.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface CompaniesPaginatedResponse {
  data: AccountRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

/**
 * Fetches companies with filtering and sorting using POST endpoint
 * @param sorting - Sort configuration
 * @param filters - Array of filter values
 * @param page - Page number (1-based)
 * @param pageSize - Number of records per page (default: 200)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with companies data, pagination info, and validation
 */
export const fetchCompaniesFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  options?: { applyGlobalRules?: boolean; reportId?: string | null }
): Promise<CompaniesPaginatedResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const effectivePageSize = pageSize ?? 200

    const requestBody: {
      data: {
        sorting?: Array<{ key: string; direction: string }>
        filters?: Array<{ key: string; value: string[] | string | { min?: number | string; max?: number | string } }>
        apply_global_rules?: boolean
        page?: number
        page_size?: number
        report_id?: string
      }
    } = {
      data: {
        page,
        page_size: effectivePageSize
      }
    }

    // Add sorting if provided
    requestBody.data.sorting = sorting.map((sort) => ({
      key: sort.key,
      direction: sort.direction
    }))

    // Add filters if provided
    if (filters.length > 0) {
      requestBody.data.filters = filters.map((filter) => ({
        key: filter.key,
        value: filter.value
      }))
    } else {
      requestBody.data.filters = []
    }

    if (options?.applyGlobalRules === false) {
      requestBody.data.apply_global_rules = false
    }

    if (options?.reportId) {
      requestBody.data.report_id = options.reportId
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.FILTER), {
      method: API_ROUTES.COMPANIES.FILTER.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch filtered companies: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number }

    const validatedData = validateAccountRecord(parsed.data || [], { isArray: true }) as AccountRecord[]

    return {
      data: validatedData,
      hasMore: parsed.has_more || false,
      totalCount: parsed.count || 0
    }
  } catch (error) {
    // Error handling - return error in response
    if (process.env.NODE_ENV === 'development') {
      console.error('Unexpected error fetching filtered companies:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching companies'
    }
  }
}

export interface CompaniesMobileListResponse {
  data: AccountMobileListRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

/**
 * Lean mobile companies list — hits `/accounts/mobile-list` (the view__accounts_mobile_list
 * RPC), which returns only id + name + investment-stats headline numbers, paginated
 * server-side. ~4–5x faster and ~12x smaller payload than `fetchCompaniesFiltered`, which
 * the thin mobile row doesn't need. `filters` is the same FilterValue[] the grid uses; the
 * RPC honours role / industry / country / city.
 */
export const fetchCompaniesMobileList = async (
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number = 25,
  accessToken: string | null = null
): Promise<CompaniesMobileListResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const requestBody = {
      data: {
        filters: filters.map((filter) => ({ key: filter.key, value: filter.value })),
        page,
        page_size: pageSize
      }
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.MOBILE_LIST), {
      method: API_ROUTES.COMPANIES.MOBILE_LIST.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch mobile companies list: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number }

    const validatedData = validateAccountMobileListRecord(parsed.data || [], { isArray: true }) as AccountMobileListRecord[]

    return {
      data: validatedData,
      hasMore: parsed.has_more || false,
      totalCount: parsed.count || 0
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Unexpected error fetching mobile companies list:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching companies'
    }
  }
}

export const fetchCompaniesFilterOptions = async (
  accessToken: string | null = null,
  requestedKeys?: string[]
): Promise<TableFilterOptionsResponse> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  const body: Record<string, unknown> = { data: {} }
  if (requestedKeys && requestedKeys.length > 0) {
    body.data = { requested_keys: requestedKeys }
  }

  const res = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.FILTER_OPTIONS), {
    method: API_ROUTES.COMPANIES.FILTER_OPTIONS.method,
    headers,
    body: JSON.stringify(body)
  })

  if (!res.ok) {
    if (res.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(`Failed to fetch filter options: ${res.status} ${res.statusText}`)
  }

  const json: unknown = await res.json()
  const parsed = json as { data?: Record<string, unknown> }
  return { data: validateFilterOptions(parsed.data || {}) }
}

export interface CreateCompanyResponse {
  data: AccountRecord | null
  created_ids?: Record<string, string>
  error?: string
}

/**
 * Creates a new company with validation
 * @param companyData - The company data to create (should be AccountRecord format)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated company record or error
 */
export const createCompany = async (
  companyData: Record<string, any>,
  accessToken: string | null = null
): Promise<CreateCompanyResponse> => {
  try {
    // Filter to only include AccountRecordFields constants (safety check)
    const validAccountRecordFields = new Set<string>(Object.values(AccountRecordFields))
    const cleanedData: Record<string, any> = {}

    Object.keys(companyData).forEach((key) => {
      if (validAccountRecordFields.has(key)) {
        let value = companyData[key]

        // Ensure aliases is always an array, not a string
        if (key === AccountRecordFields.accounts__aliases) {
          if (typeof value === 'string') {
            // Convert comma-separated string to array
            value = value
              .split(',')
              .map((alias: string) => alias.trim())
              .filter((alias: string) => alias.length > 0)
            // Set to null if empty array (for optional field)
            if (value.length === 0) {
              value = null
            }
          } else if (!Array.isArray(value)) {
            // If it's not a string or array, set to null
            value = null
          }
          // If it's already an array, keep it as is
        }

        cleanedData[key] = value
      }
    })

    // Validate request data before sending
    try {
      validateAccountRecord(cleanedData)
    } catch (validationError) {
      return {
        data: null,
        error: validationError instanceof Error ? validationError.message : 'Invalid company data provided'
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.LIST), {
      method: 'POST',
      headers,
      body: JSON.stringify(cleanedData)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 400) {
        const errorResult = await response.json()

        // Check if this is a duplicate error
        if (errorResult.message === 'Duplicate records found' && errorResult.data) {
          // Show toast for each duplicate field
          Object.entries(errorResult.data).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
              const formattedLabel = formatKeyToLabel(key, true, true)
              const duplicateValues = value.join(', ')
              showErrorToast(`Duplicate ${formattedLabel} found: ${duplicateValues}`, TOAST_CONFIG)
            }
          })
        }

        // Still throw error to prevent form submission
        throw new Error(await readBackendErrorMessage(response, 'Failed to create company', errorResult))
      }
      // Any other failure: the backend's own sentence, never a bare status line.
      throw new Error(await readBackendErrorMessage(response, 'Failed to create company'))
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown; created_ids?: Record<string, string> }

    if (parsed.data) {
      const validatedData = validateAccountRecord(parsed.data)
      return {
        data: validatedData,
        created_ids: parsed.created_ids
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    // Error handling - return error in response
    if (process.env.NODE_ENV === 'development') {
      console.error('Error creating company:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while creating company'
    }
  }
}

export interface FetchCompanyByIdResponse {
  data: AccountRecord | null
  error?: string
}

/**
 * Fetches a single company by ID
 * @param companyId - The ID of the company to fetch
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated company record or error
 */
export const fetchCompanyById = async (
  companyId: string | number,
  accessToken: string | null = null
): Promise<FetchCompanyByIdResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.GET, { id: companyId }), {
      method: API_ROUTES.COMPANIES.GET.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 404) {
        throw new Error('Company not found')
      }
      throw new Error(`Failed to fetch company: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown }

    if (parsed.data) {
      const validatedData = validateAccountRecord(parsed.data)
      return {
        data: validatedData
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    // Error handling - return error in response
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching company by ID:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching company'
    }
  }
}

export const deleteCompany = async (companyId: string | number, accessToken: string | null = null): Promise<void> => {
  const headers: Record<string, string> = {}
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }
  const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.DELETE, { id: companyId }), {
    method: API_ROUTES.COMPANIES.DELETE.method,
    headers
  })
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed - please log in again')
    }
    const errorBody = await response.json().catch(() => ({}))
    throw new Error((errorBody as { message?: string }).message || 'Failed to delete company')
  }
}

export interface UpdateCompanyResponse {
  data: AccountRecord | null
  error?: string
}

/**
 * Updates an existing company
 * Note: For form field updates, InputManager auto-patch is preferred.
 * This function is kept for programmatic updates.
 * @param companyId - The ID of the company to update
 * @param companyData - The company data to update (partial updates supported)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated company record or error
 */
export const updateCompany = async (
  companyId: string | number,
  companyData: Record<string, any>,
  accessToken: string | null = null
): Promise<UpdateCompanyResponse> => {
  try {
    // Filter to only include AccountRecordFields constants (safety check)
    const validAccountRecordFields = new Set<string>(Object.values(AccountRecordFields))
    const cleanedData: Record<string, any> = {}

    Object.keys(companyData).forEach((key) => {
      if (validAccountRecordFields.has(key)) {
        cleanedData[key] = companyData[key]
      }
    })

    // Only convert numeric fields if they were actually provided in the original data
    const numericFields = ['valuation_round']
    numericFields.forEach((field) => {
      if (cleanedData.hasOwnProperty(field)) {
        cleanedData[field] = cleanedData[field] ? parseFloat(String(cleanedData[field])) : null
      }
    })

    // Validate request data before sending (partial validation for updates)
    try {
      validateAccountRecord(cleanedData)
    } catch (validationError) {
      return {
        data: null,
        error: validationError instanceof Error ? validationError.message : 'Invalid company data provided'
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.UPDATE, { id: companyId }), {
      method: API_ROUTES.COMPANIES.UPDATE.method,
      headers,
      body: JSON.stringify(cleanedData)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 404) {
        throw new Error('Company not found')
      }
      if (response.status === 400) {
        const errorResult = await response.json()
        throw new Error((errorResult as { message?: string }).message || 'Invalid data provided')
      }
      throw new Error(`Failed to update company: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown }

    if (parsed.data) {
      const validatedData = validateAccountRecord(parsed.data)
      return {
        data: validatedData
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    // Error handling - return error in response
    if (process.env.NODE_ENV === 'development') {
      console.error('Error updating company:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while updating company'
    }
  }
}

export interface SearchCompaniesForPopupResponse {
  data: Array<{ label: string; value: string; legal_name: string | null }>
  error?: string
}

/**
 * Searches companies for toolbar search popup (independent of table filters)
 * Returns simplified format {label, value, legal_name} for display/navigation
 * @param searchTerm - The search term to look for
 * @param limit - Maximum number of results to return (default: 10)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with company search results in simplified format or error
 */
export const searchCompaniesForPopup = async (
  searchTerm: string,
  limit: number = 10,
  accessToken: string | null = null
): Promise<SearchCompaniesForPopupResponse> => {
  try {
    if (!searchTerm || searchTerm.trim() === '') {
      return { data: [] }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const queryParams = new URLSearchParams({
      search: searchTerm.trim(),
      limit: limit.toString(),
      sortColumn: AccountRecordFields.accounts__name || 'name',
      sortDirection: 'asc'
    })

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.SEARCH, {}, Object.fromEntries(queryParams)),
      {
        method: API_ROUTES.COMPANIES.SEARCH.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to search companies: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[] }

    if (parsed.data) {
      // Limit results - skip validation since backend returns simplified format {label, value, legal_name}
      // for popup display/navigation, not full AccountRecord format
      const limitedData = parsed.data.slice(0, limit)

      // Add accounts__id and accounts__name as value for drawer usage and tab usage
      const validatedData = limitedData.map((item: any) => ({
        ...item,
        [AccountRecordFields.accounts__id]: item.value,
        [AccountRecordFields.accounts__name]: item.label
      }))

      return {
        data: validatedData
      }
    }

    return {
      data: []
    }
  } catch (error) {
    // Error handling - return error in response
    if (process.env.NODE_ENV === 'development') {
      console.error('Error searching companies for popup:', error)
    }
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred while searching companies'
    }
  }
}

export interface FetchCompanySourcingContactsResponse {
  data: {
    contact_accounts__introduced_by: any[]
    contact_accounts__sourced_by: any[]
  }
  error?: string
}

/**
 * Fetches sourcing contacts (introduced_by and sourced_by) for a company
 * @param companyId - The ID of the company
 * @returns Promise with sourcing contacts data or error
 */
export const fetchCompanySourcingContacts = async (
  companyId: string | number
): Promise<FetchCompanySourcingContactsResponse> => {
  try {
    const {
      data: { session }
    } = await supabase.auth.getSession()

    if (!session?.access_token) {
      throw new Error('No authentication token available')
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json'
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.COMPANIES.SOURCING, { id: companyId }), {
      method: API_ROUTES.COMPANIES.SOURCING.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 404) {
        throw new Error('Company not found')
      }
      throw new Error(`Failed to fetch sourcing contacts: ${response.status} ${response.statusText}`)
    }

    const result = await response.json()

    return {
      data: result.data || {
        contact_accounts__introduced_by: [],
        contact_accounts__sourced_by: []
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching company sourcing contacts:', error)
    }
    return {
      data: {
        contact_accounts__introduced_by: [],
        contact_accounts__sourced_by: []
      },
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching sourcing contacts'
    }
  }
}

export interface FetchAllCompaniesResponse {
  data: AccountRecord[]
  error?: string
}

/**
 * Fetches all companies from backend API (no pagination - returns everything)
 * Used for dropdown options in deals and other modules
 * @param accessToken - User's access token from Redux store
 * @returns Promise with all companies data or error
 */
export const fetchAllCompanies = async (accessToken: string | null = null): Promise<FetchAllCompaniesResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.COMPANIES.LIST,
        {},
        {
          page: '1',
          pageSize: '10000' // Large enough to get all companies
        }
      ),
      {
        method: API_ROUTES.COMPANIES.LIST.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch companies: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[] }

    if (parsed.data) {
      const validatedData = validateAccountRecord(parsed.data, { isArray: true }) as AccountRecord[]
      return {
        data: validatedData
      }
    }

    return {
      data: []
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching companies:', error)
    }
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching companies'
    }
  }
}

export interface FetchAccountInvestmentMeasuresResponse {
  data: InvestmentMeasureRecord[]
  error?: string
}

/**
 * Fetches investment measures for a company
 * @param accountId - The ID of the company
 * @param accessToken - User's access token from Redux store
 * @returns Promise with investment measures data or error
 */
export const fetchAccountInvestmentMeasures = async (
  accountId: string | number,
  accessToken: string | null = null
): Promise<FetchAccountInvestmentMeasuresResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    } else {
      // Fallback to supabase session if no token provided
      const {
        data: { session }
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('No authentication token available')
      }

      headers['Authorization'] = `Bearer ${session.access_token}`
    }

    // Call the new reporting endpoint with POST
    const requestBody = {
      aggregation: ['fund'],
      account_ids: [accountId.toString()]
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.REPORTING.MEASURES), {
      method: API_ROUTES.REPORTING.MEASURES.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 400) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Invalid request parameters')
      }
      throw new Error(`Failed to fetch investment measures: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown }

    if (parsed.data) {
      const validatedData = validateInvestmentMeasureRecord(parsed.data, { isArray: true }) as InvestmentMeasureRecord[]
      return {
        data: validatedData
      }
    }

    return {
      data: []
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching investment measures:', error)
    }
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching investment measures'
    }
  }
}
